import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { MetricDisplay } from "../components/MetricDisplay";
import {
  MetricComparison,
  metricComparisonVariants,
} from "../components/MetricComparison";
import { BlockVariant } from "../types";

// Recipe for KeyMetricChange (based on overall_change from InsightRecipe)
const keyMetricChangeRecipe = {
  id: "key-metric-change",
  name: "Key Metric Change",
  globalKey: [],
  prefix: `{
    "metric": "string",
    "metric_unit": "string",
    "previous_value": 0,
    "current_value": 0,
    "delta": 0,
    "percentage_change": 0,
    "commentary": "string"
  }`,
  format: {
    type: "json",
    fields: [
      "metric",
      "metric_unit",
      "previous_value",
      "current_value",
      "delta",
      "percentage_change",
      "commentary",
    ],
    constraints: [
      { field: "metric", type: "string", required: true },
      { field: "previous_value", type: "number", required: true },
      { field: "current_value", type: "number", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any) => {
        try {
          const parsed = JSON.parse(data);
          return (
            parsed.metric &&
            typeof parsed.previous_value === "number" &&
            typeof parsed.current_value === "number"
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// Create variants from MetricComparison variants
const keyMetricChangeVariants: BlockVariant[] = metricComparisonVariants.map(
  (variant) => ({
    id: variant,
    name:
      variant.charAt(0).toUpperCase() +
      variant.slice(1).replace(/([A-Z])/g, " $1"),
    description: `Metric comparison using ${variant} layout`,
    defaultSettings: {},
  })
);

// Type for variant IDs
type KeyMetricChangeVariantId = (typeof metricComparisonVariants)[number];

// Parser
function keyMetricChangeParser(data: any) {
  try {
    return { data: data[keyMetricChangeBlock.id] };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse key metric change data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// Renderer
function KeyMetricChangeBlockRenderer(props: any) {
  const { data, variant = "default", className, style } = props;
  if (!data || !data[keyMetricChangeBlock.id]) return null;
  const changeData = data[keyMetricChangeBlock.id];

  return (
    <div className="vzbl-panel-container">
      <MetricComparison
        previousValue={changeData.previous_value}
        currentValue={changeData.current_value}
        changeValue={changeData.percentage_change}
        label={changeData.metric}
        unit={changeData.metric_unit}
        variant={variant as KeyMetricChangeVariantId}
        className={className}
        // style={style} // Only if MetricComparison supports it
      />
    </div>
  );
}

// Create the block
const keyMetricChangeBlock = createBaseBlock({
  id: "key-metric-change",
  name: "Key Metric Change",
  description: "Displays a key metric change with percent and commentary",
  recipe: keyMetricChangeRecipe,
  variants: keyMetricChangeVariants,
  parser: keyMetricChangeParser,
  renderer: KeyMetricChangeBlockRenderer,
});

export function KeyMetricChangeBlockProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Register the block if needed
  // useRegisterBlock(keyMetricChangeBlock);
  return <>{children}</>;
}

export { keyMetricChangeBlock };
